import * as THREE from "three";
import { LineMaterial, LineSegments2, LineSegmentsGeometry } from "three-stdlib";
import { getViewerHandles } from "../runtime/viewerHandles";
import { getMeshSceneAnchorObject } from "./sceneAnchor.js";
import {
  getRotationGizmoVisible,
  subscribeRotationGizmoVisible,
} from "../selected-body-transform-gizmo/rotationGizmoVisibility";
import { getViewerState, useViewerStore } from "../stores/viewerStore";

const AXES_NAME = "__axesHelper";
const UP_ARROW_NAME = "__upArrow";
const MESH_ORIGIN_MARKER_ROOT_NAME = "__meshOriginMarkerRoot";
const INSPECTOR_OVERLAY_ROOT = "__meshInspectorOverlayRoot";
const MULTI_SELECTION_BB_ROOT = "__multiSelectionBoundingBox";
const DEFAULT_BODY_STYLE_MARKER_BLUE = 0x3f8bff;
const BOUNDING_BOX_COLOR = 0x4ade80;
const BOUNDING_BOX_LINE_WIDTH = 4;

function createBoundingBoxOverlay(
  box: THREE.Box3,
  renderer: THREE.WebGLRenderer | null
): THREE.Object3D {
  const { min, max } = box;
  const corners = [
    [min.x, min.y, min.z],
    [max.x, min.y, min.z],
    [max.x, max.y, min.z],
    [min.x, max.y, min.z],
    [min.x, min.y, max.z],
    [max.x, min.y, max.z],
    [max.x, max.y, max.z],
    [min.x, max.y, max.z],
  ] as const;
  const edges = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 0],
    [4, 5],
    [5, 6],
    [6, 7],
    [7, 4],
    [0, 4],
    [1, 5],
    [2, 6],
    [3, 7],
  ] as const;
  const positions = edges.flatMap(([a, b]) => [...corners[a], ...corners[b]]);
  const geometry = new LineSegmentsGeometry();
  geometry.setPositions(positions);

  const material = new LineMaterial({
    color: BOUNDING_BOX_COLOR,
    linewidth: BOUNDING_BOX_LINE_WIDTH,
    depthTest: false,
    depthWrite: false,
    transparent: true,
    opacity: 0.95,
    worldUnits: false,
  });

  if (renderer) {
    const size = renderer.getSize(new THREE.Vector2());
    material.resolution.set(size.x, size.y);
  }

  const helper = new LineSegments2(geometry, material);
  helper.computeLineDistances();
  helper.renderOrder = 9999;
  helper.userData.isInspectorBoundingBox = true;
  return helper;
}

function applyAxesOverlayMaterial(obj: THREE.Object3D): void {
  const setOverlay = (o: THREE.Object3D & { material?: THREE.Material | THREE.Material[] }) => {
    if (o?.material) {
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((m) => {
        if (!m) return;
        m.depthTest = false;
        m.depthWrite = false;
        m.transparent = true;
        m.opacity = 1.0;
        m.toneMapped = false;
        (m as THREE.Material & { fog?: boolean }).fog = false;
      });
    }
  };
  setOverlay(obj as THREE.Object3D & { material?: THREE.Material | THREE.Material[] });
  if (typeof obj.traverse === "function") {
    obj.traverse((child) =>
      setOverlay(child as THREE.Object3D & { material?: THREE.Material | THREE.Material[] })
    );
  }
}

function clearRootChildren(root: THREE.Group): void {
  while (root.children.length > 0) {
    const child = root.children.pop();
    if (!child) continue;
    child.traverse(
      (obj: THREE.Object3D & { geometry?: THREE.BufferGeometry; material?: THREE.Material }) => {
        if (obj.geometry?.dispose) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => m?.dispose?.());
        }
      }
    );
  }
}

/** Prefer geom `outer` group + body so multi-body scenes don’t use the first scene match. */
function findMeshGeomRootForInspector(
  scene: THREE.Scene,
  meshUuid: string,
  bodyUuid?: string | null
): THREE.Object3D | null {
  let outerMatch: THREE.Object3D | null = null;
  let anyMatch: THREE.Object3D | null = null;
  scene.traverse((obj) => {
    const ud = obj?.userData;
    if (!ud) return;
    if (ud.meshName !== meshUuid && ud.meshUuid !== meshUuid) return;
    if (bodyUuid && String(ud.bodyUuid) !== String(bodyUuid)) return;
    if (!anyMatch) anyMatch = obj;
    if (ud.transformLayer === "outer") outerMatch = obj;
  });
  return outerMatch || anyMatch;
}

/**
 * World-space AABB of all Mesh descendants: uses Box3.setFromObject per mesh so bounds follow
 * full world transforms (avoids wrong boxes when local geometry AABB + matrix is misleading).
 */
function computeGeometryBounds(root: THREE.Object3D): THREE.Box3 | null {
  const bounds = new THREE.Box3();
  let hasGeometry = false;

  root.updateWorldMatrix(true, true);
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh?.isMesh || !mesh.geometry) return;
    const pos = mesh.geometry.getAttribute?.("position");
    if (pos && "count" in pos && pos.count === 0) return;

    const worldBox = new THREE.Box3().setFromObject(mesh);
    if (worldBox.isEmpty()) return;
    bounds.union(worldBox);
    hasGeometry = true;
  });

  return hasGeometry ? bounds : null;
}

/** Body pose’s visual subtree (geoms only): `processBodyFromStore` names it `{body}_visual`. */
function findBodyVisualGroupUnderPose(pose: THREE.Object3D | null): THREE.Object3D | null {
  if (!pose) return null;
  for (const child of pose.children) {
    if (typeof child.name === "string" && child.name.endsWith("_visual")) {
      return child;
    }
  }
  return null;
}

function removeMeshInspectorOrientationAxes(meshUuid: string, bodyUuid?: string | null): void {
  const scene = getViewerHandles().scene as THREE.Scene | null;
  if (!scene || !meshUuid) return;
  const toRemove: Array<THREE.Object3D> = [];
  const matchesRequestedBody = (obj: THREE.Object3D): boolean => {
    if (bodyUuid == null) return true;
    return String(obj.userData?.bodyUuid || "") === String(bodyUuid);
  };
  scene.traverse((obj) => {
    if (
      obj.name === MESH_ORIGIN_MARKER_ROOT_NAME &&
      obj.userData?.meshUuid === meshUuid &&
      matchesRequestedBody(obj)
    ) {
      toRemove.push(obj);
      return;
    }
    const ud = obj.userData;
    if (String(ud?.meshName || ud?.meshUuid || "") !== String(meshUuid)) return;
    if (!matchesRequestedBody(obj)) return;
    const axesExisting = obj.getObjectByName(AXES_NAME);
    const arrowExisting = obj.getObjectByName(UP_ARROW_NAME);
    if (axesExisting) toRemove.push(axesExisting);
    if (arrowExisting) toRemove.push(arrowExisting);
  });
  toRemove.forEach((child) => child.parent?.remove(child));
}

export function setMeshInspectorOrientationAxes(
  meshUuid: string,
  visible: boolean,
  bodyUuid?: string | null
): void {
  removeMeshInspectorOrientationAxes(meshUuid, bodyUuid);
  const anchor = getMeshSceneAnchorObject(meshUuid, bodyUuid ?? null);
  if (!anchor) return;
  if (!visible) return;

  const { originSphere, arrow } = buildBodyStyleFrameMarkerParts(0.1);
  const root = new THREE.Group();
  root.name = MESH_ORIGIN_MARKER_ROOT_NAME;
  root.userData = {
    isMeshOriginMarker: true,
    meshUuid,
    bodyUuid: bodyUuid ?? null,
  };
  root.renderOrder = 10002;
  root.position.set(0, 0, 0);
  root.quaternion.identity();
  root.scale.set(1, 1, 1);
  root.add(arrow);
  root.add(originSphere);
  anchor.add(root);
}

export function meshInspectorOrientationAxesVisible(meshUuid: string): boolean {
  const scene = getViewerHandles().scene as THREE.Scene | null;
  if (!scene || !meshUuid) return false;
  let found = false;
  scene.traverse((obj) => {
    if (found) return;
    if (obj.name === MESH_ORIGIN_MARKER_ROOT_NAME && obj.userData?.meshUuid === meshUuid) {
      found = true;
      return;
    }
    const ud = obj.userData;
    if (
      String(ud?.meshName || ud?.meshUuid || "") === String(meshUuid) &&
      obj.getObjectByName(AXES_NAME)
    ) {
      found = true;
      return;
    }
    if (obj.name !== AXES_NAME && obj.name !== UP_ARROW_NAME) return;
    let current = obj.parent;
    while (current) {
      const parentUserData = current.userData;
      if (String(parentUserData?.meshName || parentUserData?.meshUuid || "") === String(meshUuid)) {
        found = true;
        return;
      }
      current = current.parent;
    }
  });
  return found;
}

export function clearMeshInspectorBoundingBoxOverlays(): void {
  const scene = getViewerHandles().scene as THREE.Scene | null;
  if (!scene) return;
  const root = scene.getObjectByName(INSPECTOR_OVERLAY_ROOT);
  if (root) {
    clearRootChildren(root as THREE.Group);
    scene.remove(root);
  }
}

export function meshInspectorBoundingBoxVisible(): boolean {
  const scene = getViewerHandles().scene as THREE.Scene | null;
  if (!scene) return false;
  const root = scene.getObjectByName(INSPECTOR_OVERLAY_ROOT);
  return Boolean(root && root.children.length > 0);
}

export function renderMeshInspectorBoundingBox(meshUuid: string, bodyUuid?: string | null): void {
  clearMeshInspectorBoundingBoxOverlays();
  const { scene, renderer } = getViewerHandles();
  if (!scene || !meshUuid) return;

  const meshRoot = findMeshGeomRootForInspector(scene, meshUuid, bodyUuid ?? null);
  if (!meshRoot) return;
  const box = computeGeometryBounds(meshRoot);
  if (!box) return;

  const root = new THREE.Group();
  root.name = INSPECTOR_OVERLAY_ROOT;
  root.add(createBoundingBoxOverlay(box, renderer));
  scene.add(root);
}

export function renderMeshInspectorBoundingBoxes(
  targets: Array<{ meshUuid: string; bodyUuid?: string | null }>
): void {
  clearMeshInspectorBoundingBoxOverlays();
  const { scene, renderer } = getViewerHandles();
  if (!scene || !Array.isArray(targets) || targets.length === 0) return;

  const root = new THREE.Group();
  root.name = INSPECTOR_OVERLAY_ROOT;

  targets.forEach((target) => {
    if (!target?.meshUuid) return;
    const meshRoot = findMeshGeomRootForInspector(scene, target.meshUuid, target.bodyUuid ?? null);
    if (!meshRoot) return;
    const box = computeGeometryBounds(meshRoot);
    if (!box) return;
    root.add(createBoundingBoxOverlay(box, renderer));
  });

  if (root.children.length > 0) {
    scene.add(root);
  }
}

function findCollisionGeomRoot(
  scene: THREE.Scene,
  bodyUuid: string,
  geomUuid: string
): THREE.Object3D | null {
  let match: THREE.Object3D | null = null;
  const g = String(geomUuid);
  const b = String(bodyUuid);
  scene.traverse((obj) => {
    if (match) return;
    const ud = obj.userData;
    if (ud?.type === "collision" && String(ud.bodyUuid) === b && String(ud.geomUuid) === g) {
      match = obj;
    }
  });
  return match;
}

function getCollisionGeomSceneAnchor(bodyUuid: string, geomUuid: string): THREE.Object3D | null {
  const scene = getViewerHandles().scene as THREE.Scene | null;
  if (!scene || !bodyUuid || !geomUuid) return null;
  const root = findCollisionGeomRoot(scene, bodyUuid, geomUuid);
  if (!root) return null;
  let outer: THREE.Object3D | null = null;
  let cur: THREE.Object3D | null = root;
  while (cur?.parent) {
    if (cur.userData?.transformLayer === "outer") {
      outer = cur;
      break;
    }
    cur = cur.parent;
  }
  return outer || root;
}

export function setCollisionInspectorOrientationAxes(
  bodyUuid: string,
  geomUuid: string,
  visible: boolean
): void {
  const anchor = getCollisionGeomSceneAnchor(bodyUuid, geomUuid);
  if (!anchor) return;
  const existing = anchor.getObjectByName(AXES_NAME);
  if (existing) anchor.remove(existing);
  if (!visible) return;

  const axes = new THREE.AxesHelper(0.1);
  axes.name = AXES_NAME;
  axes.renderOrder = 9999;
  applyAxesOverlayMaterial(axes);
  anchor.add(axes);
}

export function collisionInspectorOrientationAxesVisible(
  bodyUuid: string,
  geomUuid: string
): boolean {
  const anchor = getCollisionGeomSceneAnchor(bodyUuid, geomUuid);
  if (!anchor) return false;
  return !!anchor.getObjectByName(AXES_NAME);
}

export function renderCollisionInspectorBoundingBox(bodyUuid: string, geomUuid: string): void {
  clearMeshInspectorBoundingBoxOverlays();
  const { scene, renderer } = getViewerHandles();
  if (!scene || !bodyUuid || !geomUuid) return;

  const colObj = findCollisionGeomRoot(scene, bodyUuid, geomUuid);
  if (!colObj) return;
  const box = computeGeometryBounds(colObj);
  if (!box) return;

  const root = new THREE.Group();
  root.name = INSPECTOR_OVERLAY_ROOT;
  root.add(createBoundingBoxOverlay(box, renderer));
  scene.add(root);
}

const GIZMO_BASE_SHAFT_LENGTH = 0.14;
const GIZMO_BASE_SHAFT_RADIUS = 0.0019;
const GIZMO_BASE_HEAD_LENGTH = 0.038;
const GIZMO_BASE_HEAD_RADIUS = 0.0135;

const bodyOriginMarkerWantedUuids = new Set<string>();
let showPositionGizmoUnsub: (() => void) | null = null;
let rotationGizmoUnsub: (() => void) | null = null;

function isTransformGizmoOn(): boolean {
  const pos = Boolean(getViewerState().showPositionGizmo);
  const rot = getRotationGizmoVisible();
  return pos || rot;
}

function refreshBodyOriginMarkersForGizmoChange(): void {
  if (bodyOriginMarkerWantedUuids.size === 0) return;
  for (const uuid of bodyOriginMarkerWantedUuids) {
    // Re-run application logic: shows when wanted && !gizmo; clears when gizmo is on.
    setBodyInspectorOrientationAxes(uuid, true);
  }
}

function ensureBodyOriginMarkerGizmoSubscription(): void {
  if (showPositionGizmoUnsub || rotationGizmoUnsub) return;
  showPositionGizmoUnsub = useViewerStore.subscribe((state, prevState) => {
    if (state.showPositionGizmo === prevState.showPositionGizmo) return;
    refreshBodyOriginMarkersForGizmoChange();
  });
  rotationGizmoUnsub = subscribeRotationGizmoVisible(() => {
    refreshBodyOriginMarkersForGizmoChange();
  });
}

function makeGizmoStyleArrowHeadGeometry(
  headLength: number,
  headRadius: number
): THREE.LatheGeometry {
  const points = [
    new THREE.Vector2(0, -headLength / 2),
    new THREE.Vector2(headRadius * 0.96, -headLength / 2),
    new THREE.Vector2(headRadius * 0.92, -headLength * 0.32),
    new THREE.Vector2(headRadius * 0.74, -headLength * 0.14),
    new THREE.Vector2(headRadius * 0.5, headLength * 0.02),
    new THREE.Vector2(headRadius * 0.32, headLength * 0.18),
    new THREE.Vector2(headRadius * 0.17, headLength * 0.32),
    new THREE.Vector2(headRadius * 0.07, headLength * 0.44),
    new THREE.Vector2(0, headLength / 2),
  ];
  return new THREE.LatheGeometry(points, 36);
}

function getBodyPoseGroup(bodyUuid: string): THREE.Object3D | null {
  try {
    const ref = (
      typeof window !== "undefined"
        ? (window as Window & { sceneObjectsRef?: { current?: Record<string, THREE.Object3D> } })
            .sceneObjectsRef?.current
        : undefined
    ) as Record<string, THREE.Object3D> | undefined;
    if (ref?.[bodyUuid]) return ref[bodyUuid];

    const scene = getViewerHandles().scene as THREE.Scene | null;
    if (!scene) return null;
    let match: THREE.Object3D | null = null;
    scene.traverse((obj) => {
      if (match) return;
      const ud = obj.userData;
      if (ud?.isPoseGroup && String(ud.bodyUuid) === String(bodyUuid)) {
        match = obj;
      }
    });
    return match;
  } catch {
    return null;
  }
}

type BodyStyleFrameMarkerParts = {
  originSphere: THREE.Group;
  arrow: THREE.Group;
};

function multiplyOverlayOpacity(obj: THREE.Object3D, opacityMultiplier: number): void {
  const multiplier = Math.max(0, Math.min(1, opacityMultiplier));
  obj.traverse((child) => {
    const material = (child as THREE.Object3D & { material?: THREE.Material | THREE.Material[] })
      .material;
    if (!material) return;
    const materials = Array.isArray(material) ? material : [material];
    materials.forEach((mat) => {
      if (!mat) return;
      mat.transparent = true;
      mat.opacity = Math.max(0, Math.min(1, mat.opacity * multiplier));
      mat.needsUpdate = true;
    });
  });
}

function buildBodyStyleFrameMarkerParts(
  size: number,
  markerBlue: number = DEFAULT_BODY_STYLE_MARKER_BLUE,
  opacityMultiplier: number = 1
): BodyStyleFrameMarkerParts {
  const sphereRadius = size * 0.18;
  const originSphere = new THREE.Group();
  originSphere.name = AXES_NAME;
  originSphere.renderOrder = 9999;
  const fill = new THREE.Mesh(
    new THREE.SphereGeometry(sphereRadius, 24, 16),
    new THREE.MeshBasicMaterial({ color: markerBlue })
  );
  fill.renderOrder = 9999;
  const centerDot = new THREE.Mesh(
    new THREE.SphereGeometry(sphereRadius * 0.16, 14, 10),
    new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    })
  );
  centerDot.renderOrder = 10002;
  const positiveAxisL = new THREE.Group();
  positiveAxisL.name = "__bodyOriginPositiveAxisL";
  positiveAxisL.renderOrder = 10001;
  const axisLMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.9,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  axisLMat.color.setHex(markerBlue);
  const axisLLegLength = sphereRadius * 0.85;
  const axisLRadius = sphereRadius * 0.04;
  const axisLGeometry = new THREE.CylinderGeometry(axisLRadius, axisLRadius, axisLLegLength, 8);
  const positiveXLeg = new THREE.Mesh(axisLGeometry, axisLMat);
  positiveXLeg.name = "__bodyOriginPositiveXLeg";
  positiveXLeg.position.x = axisLLegLength / 2;
  positiveXLeg.rotation.z = Math.PI / 2;
  positiveXLeg.renderOrder = 10001;
  const positiveYLeg = new THREE.Mesh(axisLGeometry.clone(), axisLMat.clone());
  positiveYLeg.name = "__bodyOriginPositiveYLeg";
  positiveYLeg.position.y = axisLLegLength / 2;
  positiveYLeg.renderOrder = 10001;
  positiveAxisL.add(positiveXLeg);
  positiveAxisL.add(positiveYLeg);
  originSphere.add(fill);
  originSphere.add(positiveAxisL);
  originSphere.add(centerDot);
  applyAxesOverlayMaterial(originSphere);
  if (fill.material) {
    fill.material.transparent = true;
    fill.material.opacity = 0.42;
  }

  const length = size * 0.9;
  const scale = length / GIZMO_BASE_SHAFT_LENGTH;
  const shaftLength = GIZMO_BASE_SHAFT_LENGTH * scale;
  const shaftRadius = GIZMO_BASE_SHAFT_RADIUS * scale * 1.45;
  const headLength = GIZMO_BASE_HEAD_LENGTH * scale;
  const headRadius = GIZMO_BASE_HEAD_RADIUS * scale;

  const arrow = new THREE.Group();
  arrow.name = UP_ARROW_NAME;
  arrow.renderOrder = 10000;
  const arrowMat = new THREE.MeshBasicMaterial({ color: markerBlue });
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(shaftRadius, shaftRadius, shaftLength, 12),
    arrowMat
  );
  shaft.position.z = shaftLength / 2;
  shaft.rotation.x = Math.PI / 2;
  shaft.renderOrder = 10000;
  const head = new THREE.Mesh(makeGizmoStyleArrowHeadGeometry(headLength, headRadius), arrowMat);
  head.position.z = shaftLength + headLength / 2;
  head.rotation.x = Math.PI / 2;
  head.renderOrder = 10000;
  arrow.add(shaft);
  arrow.add(head);
  applyAxesOverlayMaterial(arrow);
  if (opacityMultiplier < 1) {
    multiplyOverlayOpacity(originSphere, opacityMultiplier);
    multiplyOverlayOpacity(arrow, opacityMultiplier);
  }
  return { originSphere, arrow };
}

/**
 * A single grouped instance of the “body style” frame marker (semi-transparent sphere + center dot
 * + local +Z arrow), sized like the old joint `AxesHelper(0.12)`.
 */
export function createSelectedJointFrameMarkerGroup(
  size: number = 0.12,
  markerBlue: number = DEFAULT_BODY_STYLE_MARKER_BLUE
): THREE.Group {
  const { originSphere, arrow } = buildBodyStyleFrameMarkerParts(size, markerBlue);
  const root = new THREE.Group();
  root.renderOrder = 10002;
  root.add(arrow);
  root.add(originSphere);
  applyAxesOverlayMaterial(root);
  return root;
}

/** Body pose: large semi-transparent blue origin sphere + blue “up” arrow along local +Z. */
export function setBodyInspectorOrientationAxes(bodyUuid: string, visible: boolean): void {
  const b = String(bodyUuid);
  if (visible) {
    bodyOriginMarkerWantedUuids.add(b);
    ensureBodyOriginMarkerGizmoSubscription();
  } else {
    bodyOriginMarkerWantedUuids.delete(b);
  }

  const wants = bodyOriginMarkerWantedUuids.has(b);
  const pose = getBodyPoseGroup(b);
  if (!pose) {
    // If the pose object isn't in the scene yet, we still record intent above.
    return;
  }
  const existing = pose.getObjectByName(AXES_NAME);
  if (existing) pose.remove(existing);
  const upExisting = pose.getObjectByName(UP_ARROW_NAME);
  if (upExisting) pose.remove(upExisting);
  if (!wants) return;

  const opacityMultiplier = isTransformGizmoOn() ? 0.35 : 1;
  const { originSphere, arrow } = buildBodyStyleFrameMarkerParts(
    0.1,
    DEFAULT_BODY_STYLE_MARKER_BLUE,
    opacityMultiplier
  );
  pose.add(arrow);
  pose.add(originSphere);
}

export function bodyInspectorOrientationAxesVisible(bodyUuid: string): boolean {
  if (bodyOriginMarkerWantedUuids.has(String(bodyUuid))) return true;
  const pose = getBodyPoseGroup(bodyUuid);
  if (!pose) return false;
  return !!pose.getObjectByName(AXES_NAME);
}

export function renderBodyInspectorBoundingBox(bodyUuid: string): void {
  clearMeshInspectorBoundingBoxOverlays();
  const { scene, renderer } = getViewerHandles();
  const pose = getBodyPoseGroup(bodyUuid);
  if (!scene || !pose || !bodyUuid) return;

  const visual = findBodyVisualGroupUnderPose(pose);
  const box = computeGeometryBounds(visual || pose);
  if (!box) return;

  const root = new THREE.Group();
  root.name = INSPECTOR_OVERLAY_ROOT;
  root.add(createBoundingBoxOverlay(box, renderer));
  scene.add(root);
}

export function clearMultiBodySelectionBoundingBox(): void {
  const scene = getViewerHandles().scene as THREE.Scene | null;
  if (!scene) return;
  const root = scene.getObjectByName(MULTI_SELECTION_BB_ROOT);
  if (root) {
    clearRootChildren(root as THREE.Group);
    scene.remove(root);
  }
}

export function renderMultiBodySelectionBoundingBox(bodyUuids: string[]): void {
  clearMultiBodySelectionBoundingBox();
  const { scene, renderer } = getViewerHandles();
  if (!scene || !bodyUuids.length) return;

  const combined = new THREE.Box3();
  let hasGeometry = false;

  for (const uuid of bodyUuids) {
    const pose = getBodyPoseGroup(uuid);
    if (!pose) continue;
    const visual = findBodyVisualGroupUnderPose(pose);
    const box = computeGeometryBounds(visual || pose);
    if (!box) continue;
    combined.union(box);
    hasGeometry = true;
  }

  if (!hasGeometry) return;

  const root = new THREE.Group();
  root.name = MULTI_SELECTION_BB_ROOT;
  root.add(createBoundingBoxOverlay(combined, renderer));
  scene.add(root);
}
